// AI Category Detection Service using Gemini API
export interface CategorySuggestion {
  category: string
  confidence: number
  reasoning: string
}

class AICategoryService {
  private readonly GEMINI_API_KEY = import.meta.env.VITE_GEMINI_API_KEY as string
  private readonly GEMINI_API_URL = 'https://generativelanguage.googleapis.com/v1beta/models/gemini-pro:generateContent'
  
  private categoryCache = new Map<string, CategorySuggestion>()
  private readonly CACHE_DURATION = 24 * 60 * 60 * 1000 // 24 hours

  // Standardized categories for Egyptian market
  private readonly STANDARD_CATEGORIES = [
    'موبايل وتابلت',
    'تلفزيونات',
    'أجهزة كبيرة',
    'أجهزة صغيرة',
    'أجهزة المطبخ',
    'كمبيوتر',
    'الصحة والجمال',
    'إلكترونيات',
    'ادوات المنزل',
    'ملابس',
    'أحذية',
    'حقائب',
    'ساعات',
    'مجوهرات',
    'كتب',
    'ألعاب',
    'رياضة',
    'أطعمة',
    'مشروبات',
    'أدوات منزلية',
    'أثاث',
    'ديكور',
    'حديقة',
    'سيارات',
    'دراجات',
    'أدوات',
    'أدوات كهربائية',
    'أدوات يدوية',
    'أدوات طبية',
    'أدوات تعليمية'
  ]

  async detectCategory(title: string, description: string = ''): Promise<CategorySuggestion> {
    const cacheKey = `${title}-${description}`.toLowerCase()
    
    // Check cache first
    const cached = this.categoryCache.get(cacheKey)
    if (cached) {
      console.log('🤖 Using cached category detection')
      return cached
    }

    try {
      const prompt = this.buildPrompt(title, description)
      const category = await this.callGeminiAPI(prompt)
      
      const suggestion: CategorySuggestion = {
        category: this.normalizeCategory(category),
        confidence: 0.8, // Default confidence
        reasoning: `تم تحديد الفئة بناءً على: "${title}"`
      }

      // Cache the result
      this.categoryCache.set(cacheKey, suggestion)
      
      console.log('🤖 AI Category detected:', suggestion)
      return suggestion
    } catch (error) {
      console.error('❌ AI Category detection error:', error)
      
      // Fallback to simple keyword matching
      const fallbackCategory = this.fallbackCategoryDetection(title, description)
      return {
        category: fallbackCategory,
        confidence: 0.5,
        reasoning: 'تم استخدام الكلمات المفتاحية للكشف عن الفئة'
      }
    }
  }

  private buildPrompt(title: string, description: string): string {
    return `
أنت مساعد ذكي لتحديد فئات المنتجات في السوق المصري. 
يرجى تحليل اسم المنتج والوصف وتحديد الفئة المناسبة من القائمة التالية:

${this.STANDARD_CATEGORIES.join(', ')}

اسم المنتج: "${title}"
الوصف: "${description}"

يرجى الإجابة بفئة واحدة فقط من القائمة أعلاه. إذا لم تكن متأكداً، اختر "إلكترونيات" كفئة عامة.

الإجابة:`
  }

  private async callGeminiAPI(prompt: string): Promise<string> {
    const response = await fetch(`${this.GEMINI_API_URL}?key=${this.GEMINI_API_KEY}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        contents: [{
          parts: [{
            text: prompt
          }]
        }]
      })
    })

    if (!response.ok) {
      throw new Error(`Gemini API error: ${response.status}`)
    }

    const data = await response.json()
    return data.candidates?.[0]?.content?.parts?.[0]?.text?.trim() || 'إلكترونيات'
  }

  private normalizeCategory(category: string): string {
    const normalized = category.trim()
    
    // Check if it's in our standard categories
    if (this.STANDARD_CATEGORIES.includes(normalized)) {
      return normalized
    }

    // Try to find a close match
    const closeMatch = this.STANDARD_CATEGORIES.find(cat => 
      cat.includes(normalized) || normalized.includes(cat)
    )

    return closeMatch || 'إلكترونيات'
  }

  private fallbackCategoryDetection(title: string, description: string): string {
    const text = `${title} ${description}`.toLowerCase()
    
    // Mobile and tablets
    if (text.includes('موبايل') || text.includes('هاتف') || text.includes('جوال') || 
        text.includes('تابلت') || text.includes('آيباد') || text.includes('سامسونج') ||
        text.includes('شاومي') || text.includes('أيفون') || text.includes('هواوي')) {
      return 'موبايل وتابلت'
    }

    // TVs
    if (text.includes('تلفزيون') || text.includes('تلفاز') || text.includes('شاشة') ||
        text.includes('سامسونج') || text.includes('إل جي') || text.includes('سوني')) {
      return 'تلفزيونات'
    }

    // Kitchen appliances
    if (text.includes('مطبخ') || text.includes('فرن') || text.includes('ثلاجة') ||
        text.includes('غسالة') || text.includes('خلاط') || text.includes('ميكروويف')) {
      return 'أجهزة المطبخ'
    }

    // Computers
    if (text.includes('كمبيوتر') || text.includes('لابتوب') || text.includes('حاسوب') ||
        text.includes('ماك') || text.includes('ديل') || text.includes('إتش بي')) {
      return 'كمبيوتر'
    }

    // Health and beauty
    if (text.includes('صحة') || text.includes('جمال') || text.includes('عناية') ||
        text.includes('كريم') || text.includes('شامبو') || text.includes('عطر')) {
      return 'الصحة والجمال'
    }

    // Clothes
    if (text.includes('ملابس') || text.includes('قميص') || text.includes('بنطلون') ||
        text.includes('فستان') || text.includes('جاكيت') || text.includes('تي شيرت')) {
      return 'ملابس'
    }

    // Default to electronics
    return 'إلكترونيات'
  }

  // Clear cache
  clearCache() {
    this.categoryCache.clear()
    console.log('🗑️ AI Category cache cleared')
  }

  // Get cache size
  getCacheSize() {
    return this.categoryCache.size
  }

  // Get standard categories
  getStandardCategories() {
    return [...this.STANDARD_CATEGORIES]
  }
}

export const aiCategoryService = new AICategoryService()

